import { inject, Injectable } from "@angular/core";

import {
    HtmlDirective,
    HtmlDirectiveParser,
    HtmlExpression,
    HtmlExpressionParserSettings,
    HtmlExpressionParserState, HtmlExpressionType,
    HtmlForLoopDirective,
    HtmlSequence,
    HtmlText,
} from "@main/reporting.v3/widget.html/models";
import {
    HtmlBaseExpressionsParserService
} from "@main/reporting.v3/widget.html/services/html-expressions/html-base-expressions-parser.service";


@Injectable()
export class HtmlForLoopDirectiveParserService implements HtmlDirectiveParser {

    private parserService: HtmlBaseExpressionsParserService = inject(HtmlBaseExpressionsParserService);

    public readonly directive: string = "for";

    public parse(state: HtmlExpressionParserState, settings: HtmlExpressionParserSettings): HtmlDirective {
        if (!state || !settings) return null;

        if (!!settings.forbidBlocks) return null;

        const directive: HtmlDirective = this.parserService.parseDirective(state, {
            ...settings, argumentSeparator: ";"
        });

        const [iteratorExpression, ...declarationExpressions] = directive.arguments;

        const iterator: [string, HtmlExpression] = this.parseIterator(iteratorExpression);

        const mappings: Record<string, HtmlExpression> = this.parseDeclarations(declarationExpressions);

        return new HtmlForLoopDirective(directive, iterator, mappings);
    }

    public parseIterator(expression: HtmlSequence): [string, HtmlExpression] {
        if (!expression?.children?.length) return [null, null];

        const child: HtmlExpression = expression.children[0];

        // First child must be a text expression containing at least the variable name.
        if (child.type !== HtmlExpressionType.Text) return [null, null];

        const text: string = (child as HtmlText).text;

        // Match "<variable> of <list expression>" pattern where variable is any valid identifier.
        const regex: RegExp = /^\s*(?<variable>[a-zA-Z0-9_]+)\s+of\s+(?<list>[\s\S]+)?/i;

        const match: RegExpMatchArray = text?.match(regex);

        if (!match) return null;

        // Extract the variable name and list expression.
        const [_, variable, list] = match;

        if (!!list?.length) {
            // If there is a list expression in the first child, there must be no more of them.
            if (expression.children.length > 1) return [null, null];

            const data: HtmlExpression = new HtmlText(list, expression.end - list.length, expression.end);

            return [variable, data]

        } else {
            // If there is no list expression in the first child, there must be one more child.
            if (expression.children.length == 1) return [null, null];

            const data: HtmlExpression = expression.children[1];

            return [variable, data];
        }
    }

    public parseDeclarations(declarations: HtmlSequence[]): Record<string, HtmlExpression> {
        if (!declarations?.length) return null;

        // Extract variable declarations after the semicolon.
        const mappings: Record<string, HtmlExpression> = {};

        for (const declaration of declarations) {

            if (!declaration?.children?.length) continue;

            const child: HtmlExpression = declaration.children[0];

            // First child must be a text expression containing at least the variable name.
            if (child.type !== HtmlExpressionType.Text) continue;

            const text: string = (child as HtmlText).text;

            // Match "<variable> of <list expression>" pattern where variable is any valid identifier.
            const regex: RegExp = /^\s*(?<variable>[a-zA-Z0-9_]+)\s*=\s*(?<value>.+)?$/;

            const match: RegExpMatchArray = text?.match(regex);

            if (!match) continue;

            // Extract the variable name and value expression.
            const [_, variable, value] = match;

            if (!!value?.length) {
                // If there is a value expression in the first child, there must be no more of them.
                if (declaration.children.length > 1) continue;

                mappings[variable] = new HtmlText(value, declaration.end - value.length, declaration.end)
            } else {
                // If there is no value expression in the first child, there must be one more child.
                if (declaration.children.length == 1) continue;

                mappings[variable] = declaration.children[1];
            }
        }

        return mappings;
    }
}